import { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import { listFolder, ScrapbookFolder, ScrapbookFile } from '@/api';
import { ScrapbookSidebar } from './sidebar/ScrapbookSidebar';
import { ScrapbookContent } from './content/ScrapbookContent';

const CWD_COOKIE_NAME = 'STYLUS_SCRAPBOOK_CWD_ID';

interface ManagerProps {
  scrapbookPageEnabled?: boolean;
  features?: {
    scrapbookPage?: boolean;
  };
}

export function ScrapbookManager({ scrapbookPageEnabled = true, features }: ManagerProps) {
  const router = useRouter();
  const { status } = useSession();
  const [ok, setOk] = useState(true);
  const [errorredRspObj, setErrorredRspObj] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [folders, setFolders] = useState<ScrapbookFolder[]>([]);
  const [files, setFiles] = useState<ScrapbookFile[]>([]);
  const [currentFolder, setCurrentFolder] = useState<ScrapbookFolder>({
    id: 0,
    name: '',
    note: '',
    path: '',
    isShared: false,
    hasSharing: false,
    sharer: '',
    parentId: 0,
  });
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  const findFolderById = useCallback((folder: ScrapbookFolder, id: number): ScrapbookFolder | null => {
    if (folder.id === id) return folder;
    if (folder.children) {
      for (const child of folder.children) {
        const found = findFolderById(child, id);
        if (found) return found;
      }
    }
    return null;
  }, []);

  const sortFolderTree = useCallback((folder: ScrapbookFolder) => {
    if (folder.children) {
      folder.children.sort((a, b) => {
        const bothShared = a.isShared && b.isShared;
        const neitherShared = !a.isShared && !b.isShared;
        if (bothShared || neitherShared) {
          const less = a.name < b.name;
          const more = a.name > b.name;
          if (less) return -1;
          if (more) return 1;
          return 0;
        }
        return Number(a.isShared) - Number(b.isShared);
      });
      folder.children.forEach(sortFolderTree);
    }
  }, []);

  const unjsonFileContent = (files: ScrapbookFile[]) => {
    if (files) {
      files.forEach((file) => {
        // Add selection property
        (file as any).sel = false;
        if (file.content && typeof file.content === 'string') {
          try {
            file.content = JSON.parse(file.content);
          } catch {
            // Keep as string if not valid JSON
          }
        }
      });
    }
  };

  const selectDefaultFolderId = useCallback(() => {
    const fidStr = (router.query.fid as string) || getCookie(CWD_COOKIE_NAME);
    if (!fidStr) return 0;
    const fid = parseInt(fidStr);
    return isNaN(fid) ? 0 : fid;
  }, [router.query.fid]);

  const getCookie = (name: string): string | null => {
    if (typeof document === 'undefined') return null;
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
    return null;
  };

  const setCookie = (name: string, value: string) => {
    if (typeof document !== 'undefined') {
      document.cookie = `${name}=${value}; path=/`;
    }
  };

  const checkHttpRequest = (response: any) => {
    if (!response || !response.status) {
      console.error(response);
      setOk(false);
      setErrorredRspObj(response);
      return false;
    }
    if (response.status !== 200) {
      console.error(response);
      setOk(false);
      setErrorredRspObj(response);
      return false;
    }
    return true;
  };

  const fetchDirectory = useCallback(
    async (fid: number) => {
      if (loading) {
        return;
      }
      setLoading(true);

      try {
        const response = await listFolder(fid >= 0 ? fid : undefined);

        const mockResponse = { status: 200, data: response };
        if (!checkHttpRequest(mockResponse)) return;

        const listFolderResponse = response;
        const fidToFind = listFolderResponse.newFolderId;
        const folder = findFolderById(listFolderResponse.rootFolder, fidToFind);

        if (folder) {
          sortFolderTree(listFolderResponse.rootFolder);
          unjsonFileContent(listFolderResponse.files);

          setCookie(CWD_COOKIE_NAME, fidToFind.toString());

          // Update URL only if different from current query
          const currentFid = parseInt((router.query.fid as string) || '0');
          if (fidToFind !== currentFid) {
            router.replace(
              {
                pathname: router.asPath.split('?')[0],
                query: { fid: fidToFind },
              },
              undefined,
              { shallow: true },
            );
          }

          // Preserve any locally added files that aren't in the server response
          setFiles((prevFiles) => {
            const serverFiles = listFolderResponse.files;
            const localFiles = prevFiles.filter((f) => f.id && f.id > 1000000000000); // Local files have timestamp IDs
            const combinedFiles = [...serverFiles, ...localFiles];
            return combinedFiles;
          });
          setFolders([listFolderResponse.rootFolder]);
          setCurrentFolder(folder);
        } else {
          const emptyFolder = {
            id: 0,
            name: '',
            note: '',
            path: '',
            isShared: false,
            hasSharing: false,
            sharer: '',
            parentId: 0,
          };
          setCurrentFolder(emptyFolder);
          router.replace(
            {
              pathname: router.asPath.split('?')[0],
              query: {},
            },
            undefined,
            { shallow: true },
          );
          console.error("Error, can't find current folder in server response.");
        }
      } catch (error) {
        console.error('Failed to fetch directory:', error);
        setOk(false);
        setErrorredRspObj(error);
      } finally {
        setLoading(false);
      }
    },
    [loading, router, findFolderById, sortFolderTree],
  );

  const updateFolderNoteInTree = useCallback(
    (folderId: number, note: string | null) => {
      const updateFolder = (folder: ScrapbookFolder): ScrapbookFolder => {
        if (folder.id === folderId) {
          return { ...folder, note: note || '' };
        }
        if (folder.children) {
          return { ...folder, children: folder.children.map(updateFolder) };
        }
        return folder;
      };
      const updatedRoot = updateFolder(folders[0]); // assuming folders[0] is root
      setFolders([updatedRoot]);
    },
    [folders],
  );

  const handleNoteUpdate = useCallback(
    (note: string | null) => {
      updateFolderNoteInTree(currentFolder.id, note);

      // Update currentFolder as well
      const updatedCurrent = { ...currentFolder, note: note || '' };
      setCurrentFolder(updatedCurrent);
    },
    [currentFolder, updateFolderNoteInTree],
  );

  const refreshView = useCallback(async () => {
    const fid = currentFolder.id;
    await fetchDirectory(fid);
  }, [currentFolder.id, fetchDirectory]);

  const folderSelectEvent = useCallback(
    async (folder: ScrapbookFolder) => {
      if (folder.id !== currentFolder.id && !loading) {
        await fetchDirectory(folder.id);
      }
    },
    [fetchDirectory, currentFolder.id, loading],
  );

  useEffect(() => {
    if (status === 'authenticated' && !initialLoadDone) {
      const fid = selectDefaultFolderId();
      fetchDirectory(fid);
      setInitialLoadDone(true);

      // Check for pending save events in localStorage
      try {
        const savedEvent = localStorage.getItem('scrapbook-save-event');
        if (savedEvent) {
          const eventData = JSON.parse(savedEvent);

          // Process the saved event (temporarily ignore folder ID for testing)
          if (eventData.data) {
            const newFile: ScrapbookFile = {
              id: Date.now(),
              name: eventData.data.title,
              content: eventData.data,
              type: 'report',
              dateCreated: new Date().toISOString(),
              dateModified: new Date().toISOString(),
            };
            setFiles((prevFiles) => {
              const newFiles = [...prevFiles, newFile];
              return newFiles;
            });
          }
          // Clear the event after processing
          localStorage.removeItem('scrapbook-save-event');
        } else {
          console.log('ScrapbookManager: No pending save event found in localStorage');
        }
      } catch (error) {
        console.error('ScrapbookManager: Error processing localStorage event', error);
      }
    }
  }, [status, selectDefaultFolderId, fetchDirectory, initialLoadDone]);

  if (!scrapbookPageEnabled && !features?.scrapbookPage) {
    return null;
  }

  if (status === 'loading') {
    return (
      <div className="flex min-h-[80vh] items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return (
      <div className="flex min-h-[80vh] items-center justify-center">
        <div className="text-center">
          <h2 className="mb-2 text-xl font-semibold text-gray-900">Authentication Required</h2>
          <p className="mb-4 text-gray-600">Please log in to access the scrapbook.</p>
        </div>
      </div>
    );
  }

  if (!ok) {
    return (
      <div className="relative min-h-[80vh]">
        <div className="flex min-h-[80vh] items-center justify-center">
          <div className="rounded-lg border border-red-200 bg-red-50 p-8 text-center">
            <h2 className="mb-2 text-xl font-semibold text-red-600">Error</h2>
            <p className="mb-4 text-gray-600">Failed to load scrapbook data</p>
            {errorredRspObj && (
              <details className="text-left text-sm text-gray-500">
                <summary className="mb-2 cursor-pointer">Error Details</summary>
                <pre className="overflow-auto rounded bg-gray-100 p-2 text-xs">
                  {JSON.stringify(errorredRspObj, null, 2)}
                </pre>
              </details>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-[80vh]" style={{ position: 'relative' }}>
      {ok && (
        <div className="relative min-h-[80vh]">
          <div className="container mx-auto">
            <div className="flex h-full">
              <div className="w-1/4">
                <ScrapbookSidebar
                  folders={folders}
                  selectedFolderId={currentFolder.id}
                  onFolderSelect={folderSelectEvent}
                  onRefresh={refreshView}
                  currentFolder={currentFolder}
                />
              </div>
              <div className="w-3/4">
                <ScrapbookContent
                  folderName={currentFolder.name}
                  files={files}
                  loading={loading}
                  onRefresh={refreshView}
                  currentFolder={currentFolder}
                  onNoteUpdate={handleNoteUpdate}
                  onFilesUpdate={setFiles}
                  folders={folders}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
